if (!customElements.get('banzoo-cart-upsell')) {
  customElements.define(
    'banzoo-cart-upsell',
    class BanzooCartUpsell extends HTMLElement {
      connectedCallback() {
        this.scrollContainer = this.querySelector(
          '.banzoo-cart-upsell__scroll'
        );

        this.scrollRange = this.querySelector(
          '[data-banzoo-upsell-range]'
        );

        this.scrollControls = this.querySelector(
          '[data-banzoo-upsell-controls]'
        );

        this.drawerInner = this.closest(
          '.drawer__inner'
        );

        this.clickHandler = this.handleClick.bind(this);
        this.rangeHandler =
          this.handleRangeInput.bind(this);
        this.nativeScrollHandler =
          this.syncRange.bind(this);

        this.addEventListener(
          'click',
          this.clickHandler
        );

        this.scrollRange?.addEventListener(
          'input',
          this.rangeHandler
        );

        this.scrollContainer?.addEventListener(
          'scroll',
          this.nativeScrollHandler,
          { passive: true }
        );

        if (
          this.scrollContainer &&
          'ResizeObserver' in window
        ) {
          this.nativeResizeObserver =
            new ResizeObserver(() => {
              this.setScrollViewportWidth();
              this.syncRange();
            });

          this.nativeResizeObserver.observe(
            this.scrollContainer
          );

          if (this.drawerInner) {
            this.nativeResizeObserver.observe(
              this.drawerInner
            );
          }
        }

        requestAnimationFrame(() => {
          this.setScrollViewportWidth();
          this.syncRange();
        });

        this.nativeLayoutTimer = window.setTimeout(
          () => {
            this.setScrollViewportWidth();
            this.syncRange();
          },
          250
        );
      }

      disconnectedCallback() {
        this.removeEventListener(
          'click',
          this.clickHandler
        );

        this.scrollRange?.removeEventListener(
          'input',
          this.rangeHandler
        );

        this.scrollContainer?.removeEventListener(
          'scroll',
          this.nativeScrollHandler
        );

        this.nativeResizeObserver?.disconnect();

        window.clearTimeout(
          this.nativeLayoutTimer
        );
      }

      handleClick(event) {
        const directionButton = event.target.closest(
          '[data-banzoo-upsell-direction]'
        );

        if (
          directionButton &&
          this.contains(directionButton) &&
          this.scrollContainer
        ) {
          const direction = Number(
            directionButton.dataset
              .banzooUpsellDirection || 0
          );

          const firstCard = this.querySelector(
            '.banzoo-cart-upsell__card'
          );

          const track = this.querySelector(
            '.banzoo-cart-upsell__track'
          );

          const trackStyles = track
            ? window.getComputedStyle(track)
            : null;

          const gap = trackStyles
            ? parseFloat(trackStyles.columnGap) ||
              parseFloat(trackStyles.gap) ||
              0
            : 0;

          const scrollStep = firstCard
            ? firstCard.getBoundingClientRect()
                .width + gap
            : Math.max(
                this.scrollContainer.clientWidth *
                  0.75,
                140
              );

          this.scrollContainer.scrollBy({
            left: direction * scrollStep,
            behavior: 'smooth',
          });

          return;
        }

        const button = event.target.closest(
          '[data-banzoo-upsell-add]'
        );

        if (!button || !this.contains(button)) {
          return;
        }

        this.addProduct(button);
      }

      setScrollViewportWidth() {
        if (
          !this.scrollContainer ||
          !this.drawerInner
        ) {
          return;
        }

        const drawerRect =
          this.drawerInner.getBoundingClientRect();

        const scrollRect =
          this.scrollContainer.getBoundingClientRect();

        const drawerStyles = window.getComputedStyle(
          this.drawerInner
        );

        const rightPadding =
          parseFloat(drawerStyles.paddingRight) || 0;

        const availableWidth = Math.floor(
          drawerRect.right -
            scrollRect.left -
            rightPadding
        );

        if (availableWidth > 0) {
          this.scrollContainer.style.width =
            `${availableWidth}px`;

          this.scrollContainer.style.maxWidth =
            `${availableWidth}px`;
        }
      }

      handleRangeInput(event) {
        if (!this.scrollContainer) {
          return;
        }

        const maxScroll = Math.max(
          this.scrollContainer.scrollWidth -
            this.scrollContainer.clientWidth,
          0
        );

        this.scrollContainer.scrollLeft =
          (Number(event.currentTarget.value) /
            100) *
          maxScroll;
      }

      syncRange() {
        if (
          !this.scrollContainer ||
          !this.scrollRange ||
          !this.scrollControls
        ) {
          return;
        }

        const maxScroll = Math.max(
          this.scrollContainer.scrollWidth -
            this.scrollContainer.clientWidth,
          0
        );

        const cardCount = this.querySelectorAll(
          '.banzoo-cart-upsell__card'
        ).length;

        const allProductsVisible = maxScroll <= 1;

        this.scrollControls.hidden = cardCount <= 1;
        this.scrollControls.classList.toggle(
          'is-complete',
          cardCount > 1 && allProductsVisible
        );
        this.scrollRange.disabled = allProductsVisible;

        this.scrollRange.value = allProductsVisible
          ? '100'
          : maxScroll
          ? String(
              (this.scrollContainer.scrollLeft /
                maxScroll) *
                100
            )
          : '0';

        const previousButton =
          this.querySelector(
            '[data-banzoo-upsell-direction="-1"]'
          );

        const nextButton = this.querySelector(
          '[data-banzoo-upsell-direction="1"]'
        );

        if (previousButton) {
          previousButton.disabled =
            this.scrollContainer.scrollLeft <= 1;
        }

        if (nextButton) {
          nextButton.disabled =
            this.scrollContainer.scrollLeft >=
            maxScroll - 1;
        }
      }

      handlePointerDown(event) {
        if (
          !this.track ||
          !this.scrollRail ||
          !this.scrollThumb
        ) {
          return;
        }

        event.preventDefault();
        this.isDraggingScrollbar = true;
        this.dragStartX = event.clientX;
        this.dragStartScrollLeft =
          this.track.scrollLeft;

        this.scrollThumb.classList.add(
          'is-dragging'
        );

        this.scrollThumb.setPointerCapture?.(
          event.pointerId
        );
      }

      handlePointerMove(event) {
        if (
          !this.isDraggingScrollbar ||
          !this.track ||
          !this.scrollRail ||
          !this.scrollThumb
        ) {
          return;
        }

        event.preventDefault();

        const maxScroll = Math.max(
          this.track.scrollWidth -
            this.track.clientWidth,
          0
        );

        const maxThumbTravel = Math.max(
          this.scrollRail.clientWidth -
            this.scrollThumb.offsetWidth,
          1
        );

        const scrollPerPixel =
          maxScroll / maxThumbTravel;

        this.track.scrollLeft =
          this.dragStartScrollLeft +
          (event.clientX - this.dragStartX) *
            scrollPerPixel;
      }

      handlePointerUp() {
        if (!this.isDraggingScrollbar) {
          return;
        }

        this.isDraggingScrollbar = false;

        this.scrollThumb?.classList.remove(
          'is-dragging'
        );
      }

      updateScrollbar() {
        if (
          !this.track ||
          !this.scrollbar ||
          !this.scrollThumb
        ) {
          return;
        }

        const maxScroll = Math.max(
          this.track.scrollWidth -
            this.track.clientWidth,
          0
        );

        const cardCount = this.track.querySelectorAll(
          '.banzoo-cart-upsell__card'
        ).length;

        this.scrollbar.hidden = cardCount <= 1;

        if (maxScroll <= 1) {
          this.scrollThumb.style.width = '32%';
          this.scrollThumb.style.transform =
            'translate3d(0, 0, 0)';

          return;
        }

        const rail = this.scrollbar.querySelector(
          '.banzoo-cart-upsell__scroll-rail'
        );

        const previousButton =
          this.scrollbar.querySelector(
            '[data-banzoo-upsell-scroll="-1"]'
          );

        const nextButton =
          this.scrollbar.querySelector(
            '[data-banzoo-upsell-scroll="1"]'
          );

        if (!rail) {
          return;
        }

        const railWidth = rail.clientWidth;

        const calculatedThumbWidth =
          railWidth *
          (this.track.clientWidth /
            this.track.scrollWidth);

        const thumbWidth = Math.max(
          Math.min(
            calculatedThumbWidth,
            railWidth * 0.34
          ),
          40
        );

        const boundedThumbWidth = Math.min(
          thumbWidth,
          railWidth
        );

        const maxThumbTravel = Math.max(
          railWidth - boundedThumbWidth,
          0
        );

        const progress = maxScroll
          ? this.track.scrollLeft / maxScroll
          : 0;

        const boundedProgress = Math.max(
          0,
          Math.min(progress, 1)
        );

        this.scrollThumb.style.width =
          `${boundedThumbWidth}px`;

        this.scrollThumb.style.transform =
          `translate3d(${boundedProgress * maxThumbTravel}px, 0, 0)`;

        if (previousButton) {
          previousButton.disabled =
            this.track.scrollLeft <= 1;
        }

        if (nextButton) {
          nextButton.disabled =
            this.track.scrollLeft >= maxScroll - 1;
        }
      }

      async fetchCurrentCart() {
        const shopRoot =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${shopRoot}cart.js`,
          {
            headers: {
              Accept: 'application/json',
              'X-Requested-With':
                'XMLHttpRequest',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            'De actuele voorraad kon niet worden gecontroleerd. Probeer het opnieuw.'
          );
        }

        return response.json();
      }

      getCartVariantQuantity(cart, variantId) {
        return (cart?.items || []).reduce(
          (quantity, item) =>
            Number(item.id) === Number(variantId)
              ? quantity +
                Number(item.quantity || 0)
              : quantity,
          0
        );
      }

      hasEnoughInventory(button, cart) {
        if (
          button.dataset.inventoryTracked !== 'true'
        ) {
          return true;
        }

        const inventoryQuantity = Number(
          button.dataset.inventoryQuantity
        );

        const cartQuantity =
          this.getCartVariantQuantity(
            cart,
            button.dataset.variantId
          );

        return cartQuantity + 1 <= inventoryQuantity;
      }

      markUnavailable(button) {
        const card = button.closest(
          '.banzoo-cart-upsell__card'
        );

        const buttonLabel = button.querySelector(
          '.banzoo-cart-upsell__button-label'
        );

        button.disabled = true;
        button.classList.remove('is-loading');
        button.setAttribute(
          'aria-disabled',
          'true'
        );

        if (buttonLabel) {
          buttonLabel.textContent =
            'Niet meer op voorraad';
        }

        card?.classList.add('is-unavailable');
      }

      async addProduct(button) {
        const variantId = Number(
          button.dataset.variantId
        );

        const discountCode =
          button.dataset.discountCode || '';

        const applyDiscount =
          button.dataset.applyDiscount === 'true';

        if (!variantId) {
          this.showMessage(
            'Dit product kon niet worden toegevoegd.'
          );

          return;
        }

        const buttonLabel = button.querySelector(
          '.banzoo-cart-upsell__button-label'
        );

        const originalButtonText =
          buttonLabel?.textContent || '';

        button.disabled = true;
        button.classList.add('is-loading');

        if (buttonLabel) {
          buttonLabel.textContent = 'Toevoegen...';
        }

        this.showMessage();

        const cartUi =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        const sections =
          cartUi
            ?.getSectionsToRender()
            ?.map((section) => section.id) || [
            'cart-icon-bubble',
          ];

        try {
          const currentCart =
            await this.fetchCurrentCart();

          if (
            !this.hasEnoughInventory(
              button,
              currentCart
            )
          ) {
            const inventoryError = new Error(
              'Het laatste exemplaar zit al in je winkelwagen. Kies een ander product.'
            );

            inventoryError.inventoryError = true;
            throw inventoryError;
          }

          const addResponse = await fetch(
            window.routes.cart_add_url,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity: 1,
                  },
                ],
                sections,
                sections_url: window.location.pathname,
              }),
            }
          );

          let result = await addResponse.json();

          if (!addResponse.ok || result.status) {
            const inventoryError = new Error(
              addResponse.status === 422
                ? 'Dit product is net uitverkocht of het laatste exemplaar zit al in je winkelwagen. Kies een ander product.'
                : result.description ||
                    result.message ||
                    'Toevoegen is mislukt.'
            );

            inventoryError.inventoryError =
              addResponse.status === 422;

            throw inventoryError;
          }

          if (applyDiscount && discountCode) {
            try {
              result = await this.applyDiscount(
                discountCode,
                sections
              );
            } catch (discountError) {
              console.error(discountError);

              this.showMessage(
                'Het product is toegevoegd, maar de korting kon niet worden toegepast. Controleer je winkelwagen.'
              );
            }
          }

          if (
            typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined'
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'banzoo-cart-upsell',
              productVariantId: variantId,
              cartData: result,
            });
          }

          if (!cartUi) {
            window.location.href =
              window.routes.cart_url;

            return;
          }

          cartUi.setActiveElement?.(button);
          cartUi.renderContents(result);
          cartUi.classList.remove('is-empty');
        } catch (error) {
          if (error.inventoryError) {
            this.markUnavailable(button);
          }

          this.showMessage(
            error.message ||
              'Er ging iets mis. Probeer het opnieuw.'
          );

          if (!error.inventoryError) {
            button.disabled = false;
            button.classList.remove('is-loading');
          }

          if (
            buttonLabel &&
            !error.inventoryError
          ) {
            buttonLabel.textContent =
              originalButtonText;
          }
        }
      }

      async applyDiscount(
        discountCode,
        sections
      ) {
        const shopRoot =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${shopRoot}cart/update.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              discount: discountCode,
              sections,
              sections_url: window.location.pathname,
            }),
          }
        );

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'De bundelkorting kon niet worden toegepast.'
          );
        }

        return result;
      }

      showMessage(text = '') {
        const message = this.querySelector(
          '.banzoo-cart-upsell__message'
        );

        if (!message) return;

        message.textContent = text;
        message.hidden = !text;
      }
    }
  );
}

if (!customElements.get('banzoo-cart-trust')) {
  customElements.define(
    'banzoo-cart-trust',
    class BanzooCartTrust extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-banzoo-trust-track]');
        this.items = Array.from(
          this.querySelectorAll('.banzoo-cart-trust__item')
        );
        this.previousButton = this.querySelector(
          '[data-banzoo-trust-previous]'
        );
        this.nextButton = this.querySelector('[data-banzoo-trust-next]');
        this.index = 0;

        if (!this.track || this.items.length < 2) return;

        this.previousHandler = () => this.show(this.index - 1);
        this.nextHandler = () => this.show(this.index + 1);
        this.pauseHandler = () => this.stopAutoplay();
        this.resumeHandler = () => this.startAutoplay();

        this.previousButton?.addEventListener(
          'click',
          this.previousHandler
        );
        this.nextButton?.addEventListener('click', this.nextHandler);
        this.addEventListener('mouseenter', this.pauseHandler);
        this.addEventListener('mouseleave', this.resumeHandler);
        this.addEventListener('focusin', this.pauseHandler);
        this.addEventListener('focusout', this.resumeHandler);

        this.show(0);
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener(
          'click',
          this.previousHandler
        );
        this.nextButton?.removeEventListener('click', this.nextHandler);
        this.removeEventListener('mouseenter', this.pauseHandler);
        this.removeEventListener('mouseleave', this.resumeHandler);
        this.removeEventListener('focusin', this.pauseHandler);
        this.removeEventListener('focusout', this.resumeHandler);
        this.stopAutoplay();
      }

      show(nextIndex) {
        if (!this.items?.length || !this.track) return;

        this.index =
          (nextIndex + this.items.length) % this.items.length;

        this.track.style.transform =
          `translate3d(-${this.index * 100}%, 0, 0)`;
      }

      startAutoplay() {
        this.stopAutoplay();

        if (
          this.items?.length < 2 ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          return;
        }

        this.autoplayTimer = window.setInterval(() => {
          this.show(this.index + 1);
        }, 4500);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}
